import { createTranslator } from 'short-uuid';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import {
  usePassportRequests as usePassportRequestsService,
  usePassportOffers as usePassportOffersService
} from 'services/passport';

import { useUserSelector } from 'hooks/useUser';
import { getPostHog } from 'utils/deferredAnalytics';

const PassportContext = React.createContext(null);

export function PassportContextProvider({ children }) {
  const { user } = useUserSelector();
  const params = useParams();
  // Use useFlags (not useFlagsWithInit) to avoid triggering LD init on all protected routes
  // Default value (false) returns stage3Submitted=true, which is a safe fallback
  const {
    data: passportRequests = [],
    isLoading: isPassportRequestsLoading,
    refetch: reloadPassportRequests
  } = usePassportRequestsService({
    enabled: !!user?.user_id && !!user?.profile_setup_completed
  });
  const {
    data: passportOffers = [],
    isLoading: isPassportOffersLoading,
    refetch: reloadPassportOffers
  } = usePassportOffersService({
    enabled: !!passportRequests?.length
  });
  const [passportOffersLoadingEffect, setPassportOffersLoadingEffect] = useState(false);

  const passportRequest = useMemo(() => passportRequests?.[0], [passportRequests]);

  const docusignDocuments = useMemo(() => {
    if (!passportRequest?.docusign_documents?.length) {
      return [];
    }

    const { docusign_documents } = passportRequest;
    const documents = [];

    for (let i = 0; i < docusign_documents.length; i++) {
      const docusignDocument = docusign_documents[i];

      if (docusignDocument.status !== 'Completed') {
        documents.push(docusignDocument);
      }
    }

    return documents;
  }, [passportRequest]);

  const completedDocusignDocuments = useMemo(() => {
    if (!passportRequest?.docusign_documents?.length) {
      return [];
    }

    const { docusign_documents } = passportRequest;
    const documents = [];

    for (let i = 0; i < docusign_documents.length; i++) {
      const docusignDocument = docusign_documents[i];

      if (docusignDocument.status === 'Completed') {
        documents.push(docusignDocument);
      }
    }

    return documents;
  }, [passportRequest]);

  const hasUploadedDocuments = useMemo(() => {
    if (!passportRequests?.length) {
      return false;
    }

    const passportRequest = passportRequests?.[0];

    return !!(passportRequest?.id_document?.length || passportRequest?.proof_of_income?.length);
  }, [passportRequests]);

  const stage3Submitted = useMemo(() => {
    if (!passportRequests?.length) {
      return false;
    }

    const passportRequest = passportRequests?.[0];

    return !!passportRequest?.questionnaire_completed;
  }, [passportRequests]);

  const hasSkippedToBookCall = useMemo(
    () =>
      !!passportRequests?.some((request) => request?.skip_application_clicked),
    [passportRequests]
  );

  const stage2Submitted = useMemo(() => {
    if (!passportRequests?.length) {
      return false;
    }

    const passportRequest = passportRequests?.[0];

    return !!passportRequest?.stage2_submitted;
  }, [passportRequests]);

  const mortgageCountry = useMemo(() => {
    const fromUrl = params?.country_code;
    const fromData = passportRequests?.[0]?.property_country?.country_code;
    const code = fromUrl || fromData;
    return code ? code.toUpperCase() : null;
  }, [params, passportRequests]);

  // Push mortgage_country into PostHog's flag context synchronously during
  // render. Doing this in a useEffect loses the race for child flag hooks —
  // child effects fire before parent effects, so usePostHogFlag would
  // evaluate before the property was set. The ref dedups across re-renders.
  const lastSyncedCountryRef = useRef(null);
  if (mortgageCountry && lastSyncedCountryRef.current !== mortgageCountry) {
    const posthog = getPostHog();
    if (posthog) {
      posthog.setPersonPropertiesForFlags({ mortgage_country: mortgageCountry });
      lastSyncedCountryRef.current = mortgageCountry;
    }
  }

  // Persist mortgage_country to the server-side person profile via $set.
  useEffect(() => {
    if (!mortgageCountry) return;
    const posthog = getPostHog();
    if (!posthog) return;
    posthog.people.set({ mortgage_country: mortgageCountry });
  }, [mortgageCountry]);

  const documentsUploadSecret = useMemo(() => {
    if (!passportRequests?.length) {
      return null;
    }

    const translator = createTranslator();
    const passportRequest = passportRequests?.[0];

    if (passportRequest.request_id) {
      return translator.fromUUID(passportRequest.request_id);
    }
  }, [passportRequests]);

  return (
    <PassportContext.Provider
      value={{
        passportRequest,

        passportRequests,
        isPassportRequestsLoading,
        reloadPassportRequests,

        passportOffers,
        isPassportOffersLoading,
        reloadPassportOffers,
        hasUploadedDocuments,

        passportOffersLoadingEffect,
        setPassportOffersLoadingEffect,

        documentsUploadSecret,

        docusignDocuments,
        completedDocusignDocuments,
        stage3Submitted,
        stage2Submitted,
        hasSkippedToBookCall,
        mortgageCountry
      }}>
      {children}
    </PassportContext.Provider>
  );
}

export function usePassport() {
  const context = useContext(PassportContext);

  if (!context) {
    throw new Error('usePassport must be used within a PassportContextProvider');
  }

  return context;
}
